import React, { useState } from 'react';
import { useLanguage } from '../App';
import axios from 'axios';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [errors, setErrors] = useState({});

  const services = [
    t.services.soilTesting.title,
    t.services.fertilizers.title,
    t.services.farmVisits.title,
    t.services.marketing.title,
    'Other',
    'General Query'
  ];

  // Validate Indian phone number format
  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setStatus({ type: '', message: '' });
    try {
      const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      await axios.post(`${API_URL}/contact/add-contact-info`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000 // 10 second timeout
      });
      setStatus({ type: 'success', message: t.contact.success || 'Thank you! We will contact you soon.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Form submission error:', error);
      let errorMessage = t.contact.error || 'Something went wrong. Please try again.';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      }
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-green-500 text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.contact.title}</h1>
          <p className="text-xl text-green-100">{t.contact.subtitle}</p>
        </div>
      </section>
      {/* Contact Content */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-green-800 mb-6">{t.contact.getInTouch}</h2>
              <p className="text-gray-600 mb-6">{t.contact.description}</p>
              {status.message && (
                <div 
                  className={`mb-6 p-4 rounded-lg ${
                    status.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                  role="alert"
                  aria-live="polite"
                >
                  {status.message}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div>
                  <label htmlFor="name" className="form-label">{t.contact.form.name} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    aria-invalid={errors.name ? 'true' : 'false'}
                    className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                    placeholder={t.contact.form.name}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="form-label">{t.contact.form.email}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder={t.contact.form.email}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="phone" className="form-label">{t.contact.form.phone} *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    pattern="[6-9]\d{9}"
                    maxLength="10"
                    className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="9876543210"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.phone}</p>
                  )}
                  {!errors.phone && (
                    <p className="mt-1 text-xs text-gray-500">Enter 10-digit mobile number without +91</p>
                  )}
                </div>
                <div>
                  <label htmlFor="service" className="form-label">{t.contact.form.service}</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">{t.contact.form.selectService}</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="form-label">{t.contact.form.message} *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    aria-invalid={errors.message ? 'true' : 'false'}
                    rows="4"
                    className={`form-input ${errors.message ? 'border-red-500' : ''}`}
                    placeholder={t.contact.form.message}
                  ></textarea>
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-busy={loading}
                >
                  {loading ? (t.contact.form.sending || 'Sending...') : (t.contact.form.submit || 'Submit')}
                </button>
              </form>
            </div>
            {/* Contact Information */}
            <div className="space-y-6">{/* ... unchanged ... */}</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
